const INTERACTIVE_SELECTOR = "[data-cursor-interaction]";
const OUTLINE_DAMPING = 8;

export class CursorController {
  readonly #container: HTMLElement;
  readonly #dot: HTMLElement;
  readonly #outline: HTMLElement;
  readonly #finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
  #animationFrame = 0;
  #enabled = false;
  #targetX = window.innerWidth / 2;
  #targetY = window.innerHeight / 2;
  #outlineX = this.#targetX;
  #outlineY = this.#targetY;
  #lastFrameTime?: number;

  readonly #handlePointerMove = (event: PointerEvent): void => {
    this.#targetX = event.clientX;
    this.#targetY = event.clientY;
    this.#dot.style.transform = `translate3d(${this.#targetX}px, ${this.#targetY}px, 0)`;
    this.#container.classList.add("is-visible");

    const target = event.target instanceof Element ? event.target : null;
    this.#container.classList.toggle("is-interactive", Boolean(target?.closest(INTERACTIVE_SELECTOR)));
  };

  readonly #handlePointerLeave = (): void => {
    this.#container.classList.remove("is-visible", "is-interactive");
  };

  readonly #handlePointerDown = (): void => {
    this.#container.classList.add("is-pressed");
  };

  readonly #handlePointerUp = (): void => {
    this.#container.classList.remove("is-pressed");
  };

  readonly #handleMediaChange = (): void => {
    if (this.#finePointer.matches) this.#enable();
    else this.#disable();
  };

  constructor(container: HTMLElement, dot: HTMLElement, outline: HTMLElement) {
    this.#container = container;
    this.#dot = dot;
    this.#outline = outline;
  }

  mount(): void {
    this.#finePointer.addEventListener("change", this.#handleMediaChange);
    this.#handleMediaChange();
  }

  destroy(): void {
    this.#finePointer.removeEventListener("change", this.#handleMediaChange);
    this.#disable();
  }

  #enable(): void {
    if (this.#enabled) return;
    this.#enabled = true;
    document.documentElement.classList.add("has-custom-cursor");
    window.addEventListener("pointermove", this.#handlePointerMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", this.#handlePointerLeave);
    window.addEventListener("pointerdown", this.#handlePointerDown, { passive: true });
    window.addEventListener("pointerup", this.#handlePointerUp, { passive: true });
    this.#animationFrame = requestAnimationFrame(this.#animateOutline);
  }

  #disable(): void {
    if (!this.#enabled) return;
    this.#enabled = false;
    document.documentElement.classList.remove("has-custom-cursor");
    window.removeEventListener("pointermove", this.#handlePointerMove);
    document.documentElement.removeEventListener("pointerleave", this.#handlePointerLeave);
    window.removeEventListener("pointerdown", this.#handlePointerDown);
    window.removeEventListener("pointerup", this.#handlePointerUp);
    cancelAnimationFrame(this.#animationFrame);
    this.#lastFrameTime = undefined;
    this.#container.classList.remove("is-visible", "is-interactive", "is-pressed");
  }

  readonly #animateOutline = (frameTime: number): void => {
    const previousFrameTime = this.#lastFrameTime ?? frameTime - 1_000 / 60;
    const deltaSeconds = Math.min(Math.max((frameTime - previousFrameTime) / 1_000, 0), 1);
    const blend = 1 - Math.exp(-OUTLINE_DAMPING * deltaSeconds);
    this.#lastFrameTime = frameTime;
    this.#outlineX += (this.#targetX - this.#outlineX) * blend;
    this.#outlineY += (this.#targetY - this.#outlineY) * blend;
    this.#outline.style.transform = `translate3d(${this.#outlineX}px, ${this.#outlineY}px, 0)`;
    this.#animationFrame = requestAnimationFrame(this.#animateOutline);
  };
}
